import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Checkbox,
  FormControl,
  FormHelperText,
  Grid,
  IconButton,
  MenuItem,
  Pagination,
  Paper,
  Select,
  styled,
  Table,
  TableBody,
  TableCell,
  tableCellClasses,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  Add,
  ExpandMore,
  ExpandLess,
  Delete,
  Edit,
  EditOff,
  Check,
  Clear,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import {
  Controller,
  FormProvider,
  useFieldArray,
  useForm,
  useFormContext,
} from "react-hook-form";
import dayjs from "dayjs";
import {
  Groups,
  RecordUserDataModel,
  Roles,
  UserGroup,
  UserProfiles,
} from "../../../type/response/RecordUserDataInitResponse";
import {
  columnLabels,
  formDefaultValue,
  getUserGroupStr,
  getUserProfilesStr,
  getUserRolesStr,
  inputPattern,
} from "../UserManagement";

/**
 * テーブルのスタイル(セル)を設定
 */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.custom.gray,
    color: theme.palette.common.white,
    whiteSpace: "nowrap", // 改行しない
    padding: "6px 4px",
  },
  [`&.${tableCellClasses.body}`]: {
    whiteSpace: "normal",
    padding: "6px 4px",
  },
}));

/**
 * テーブルのスタイル(行)を設定
 */
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:last-child td, &:last-child th": {
    border: 0,
  },
}));

/**
 * テーブルの行設定
 */
const CustomTableRow = memo(
  ({
    initRow,
    selectedRows,
    setSelectedRows,
    editRows,
    setEditRows,
    visibleColumns,
    rolesOptions,
    groupsOptions,
    handleUpdateRow,
    allowEmailEdit,
  }: {
    initRow: RecordUserDataModel;
    index: number;
    selectedRows: Set<string>;
    setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
    editRows: Set<string>;
    setEditRows: React.Dispatch<React.SetStateAction<Set<string>>>;
    visibleColumns: Set<string>;
    rolesOptions: Roles[];
    groupsOptions: Groups[];
    handleUpdateRow: (row: RecordUserDataModel) => void;
    allowEmailEdit: boolean;
  }) => {
    const form = useForm<RecordUserDataModel>({
      mode: "onChange",
      defaultValues: formDefaultValue,
    });
    const {
      control,
      register,
      reset,
      getValues,
      handleSubmit,
      formState: { errors },
    } = form;

    useEffect(() => {
      if (editRows.has(initRow.userId ?? "")) reset(initRow);
    }, [editRows, initRow, reset]);

    const handleModeChange = useCallback(() => {
      const newSet = new Set(editRows);
      newSet.has(initRow.userId ?? "")
        ? newSet.delete(initRow.userId ?? "")
        : newSet.add(initRow.userId ?? "");
      setEditRows(newSet);
    }, [editRows, initRow.userId, setEditRows]);

    return (
      <StyledTableRow
        sx={{
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "#e3f2fd", // 浅いブルー
            boxShadow: "inset 0 0 0 1px #90caf9",
          },
        }}
      >
        <StyledTableCell sx={{ verticalAlign: "top" }}>
          <Checkbox
            checked={selectedRows.has(initRow.userId ?? "")}
            onChange={(e) => {
              e.stopPropagation(); // クリックイベントを防止
              const newSet = new Set(selectedRows);
              newSet.has(initRow.userId ?? "")
                ? newSet.delete(initRow.userId ?? "")
                : newSet.add(initRow.userId ?? "");
              setSelectedRows(newSet);
            }}
          />
        </StyledTableCell>
        <StyledTableCell sx={{ verticalAlign: "top" }}>
          {editRows.has(initRow.userId ?? "") ? (
            <Tooltip title="元に戻す">
              <IconButton
                type="button"
                onClick={() => {
                  reset(initRow);
                  handleModeChange();
                }}
                sx={{ mt: 0.25 }}
              >
                <Clear fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="編集モードに切替">
              <IconButton
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  handleModeChange();
                }}
                sx={{ mt: 0.25 }}
              >
                <Edit fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </StyledTableCell>
        <StyledTableCell sx={{ verticalAlign: "top" }}>
          {editRows.has(initRow.userId ?? "") ? (
            <Tooltip title="確定">
              <IconButton
                type="button"
                onClick={handleSubmit(() => {
                  handleUpdateRow(getValues());
                  handleModeChange();
                })}
                sx={{ mt: 0.25, mr: 1.25 }}
              >
                <Check fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : (
            <Box sx={{ width: 36, height: 36, mt: 0.25, mr: 1.25 }} />
          )}
        </StyledTableCell>
        <FormProvider {...form}>
          {Object.keys(columnLabels)
            .filter((key) => visibleColumns.has(key))
            .map((key) => {
              const render = () => {
                switch (key) {
                  case "firstName":
                  case "lastName":
                    return (
                      <Box sx={{ width: 90, mt: 0.5 }}>
                        {editRows.has(initRow.userId ?? "") ? (
                          <TextField
                            {...register(key, {
                              required: "この項目は入力必須です。",
                            })}
                            error={!!errors[key as keyof RecordUserDataModel]}
                            helperText={
                              errors[key as keyof RecordUserDataModel]?.message
                            }
                            aria-label={key}
                            fullWidth
                            size="small"
                          />
                        ) : (
                          `${initRow[key as keyof RecordUserDataModel] ?? ""}`
                        )}
                      </Box>
                    );
                  case "middleName":
                  case "nickname":
                    return (
                      <Box sx={{ width: 90, mt: 0.5 }}>
                        {editRows.has(initRow.userId ?? "") ? (
                          <TextField
                            {...register(key)}
                            aria-label={key}
                            fullWidth
                            size="small"
                          />
                        ) : (
                          `${initRow[key as keyof RecordUserDataModel] ?? ""}`
                        )}
                      </Box>
                    );
                  case "email":
                    return (
                      <Box sx={{ width: 200, mt: 0.5 }}>
                        {editRows.has(initRow.userId ?? "") && allowEmailEdit ? (
                          <TextField
                            {...register("email", {
                              required: "この項目は入力必須です。",
                              pattern: {
                                value: inputPattern.email,
                                message: "フォーマットが正しくありません。",
                              },
                            })}
                            error={!!errors.email}
                            helperText={errors.email?.message}
                            aria-label={key}
                            fullWidth
                            size="small"
                          />
                        ) : (
                          `${initRow.email ?? ""}`
                        )}
                      </Box>
                    );
                  case "userRoles":
                    return (
                      <Box sx={{ width: 300, mt: 0.5 }}>
                        {editRows.has(initRow.userId ?? "") ? (
                          <Controller
                            control={control}
                            name="userRoles"
                            rules={{ required: "この項目は入力必須です。" }}
                            render={({ field }) => (
                              <Autocomplete
                                multiple
                                options={rolesOptions}
                                getOptionLabel={(option: Roles) => option.name}
                                value={field.value || []}
                                onChange={(_, newValue) => field.onChange(newValue)}
                                renderInput={(params) => (
                                  <TextField
                                    {...params}
                                    required
                                    error={!!errors.userRoles}
                                    helperText={errors.userRoles?.message}
                                    size="small"
                                  />
                                )}
                              />
                            )}
                          />
                        ) : (
                          getUserRolesStr(initRow.userRoles)
                        )}
                      </Box>
                    );
                  case "userProfiles":
                    return (
                      <Box sx={{ width: 350, mt: 0.5 }}>
                        {editRows.has(initRow.userId ?? "") ? (
                          <UserProfilesCellRender />
                        ) : (
                          getUserProfilesStr(
                            initRow.userProfiles?.filter((v) => !v.isDeleted) ??
                              []
                          )
                        )}
                      </Box>
                    );
                  case "userGroup":
                    return (
                      <Box sx={{ width: 820, mt: 0.5 }}>
                        {editRows.has(initRow.userId ?? "") ? (
                          <UserGroupCellRender groupsOptions={groupsOptions} />
                        ) : (
                          getUserGroupStr(initRow.userGroup)
                        )}
                      </Box>
                    );
                  default:
                    return null;
                }
              };
              return (
                <StyledTableCell
                  key={key}
                  align="left"
                  sx={{
                    px: 1,
                    verticalAlign: editRows.has(initRow.userId ?? "")
                      ? "top"
                      : "middle",
                  }}
                >
                  {render()}
                </StyledTableCell>
              );
            })}
        </FormProvider>
      </StyledTableRow>
    );
  }
);

/**
 * ユーザー情報セルレンダー
 */
const UserProfilesCellRender = memo(() => {
  const {
    control,
    formState: { errors },
  } = useFormContext<RecordUserDataModel>();
  const { fields, append, remove } = useFieldArray({
    control,
    name: "userProfiles",
  });
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        rowGap: 1,
      }}
    >
      {fields
        .filter((v) => !v.isDeleted)
        .map((_, index) => (
          <Grid
            container
            columnGap={0.25}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Grid>
              <Controller
                control={control}
                name={`userProfiles.${index}.key`}
                rules={{
                  required: "この項目は入力必須です。",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    error={!!errors.userProfiles?.[index]?.key}
                    helperText={errors.userProfiles?.[index]?.key?.message}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="キー"
                    sx={{ width: "4.75rem" }}
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid>
              <Typography variant="body2">:</Typography>
            </Grid>
            <Grid>
              <Controller
                control={control}
                name={`userProfiles.${index}.value`}
                rules={{
                  required: "この項目は入力必須です。",
                }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    required
                    error={!!errors.userProfiles?.[index]?.value}
                    helperText={errors.userProfiles?.[index]?.value?.message}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="値"
                    sx={{ width: "13.75rem" }}
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid>
              <IconButton type="button" onClick={() => remove(index)}>
                <Delete fontSize="small" />
              </IconButton>
            </Grid>
          </Grid>
        ))}
      <IconButton
        type="button"
        color="primary"
        onClick={() =>
          append({
            id: null,
            key: "",
            value: "",
            isDeleted: false,
          } as UserProfiles)
        }
      >
        <Add fontSize="small" />
      </IconButton>
    </Box>
  );
});

/**
 * ユーザー所属情報セルレンダー
 */
const UserGroupCellRender = memo(
  ({ groupsOptions }: { groupsOptions: Groups[] }) => {
    const {
      control,
      formState: { errors },
    } = useFormContext<RecordUserDataModel>();
    const { fields, append, remove } = useFieldArray({
      control,
      name: "userGroup",
    });
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          rowGap: 1,
        }}
      >
        {fields.map((value, index) => (
          <Grid
            container
            columnGap={0.25}
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
            }}
          >
            <Grid>
              <Controller
                control={control}
                name={`userGroup.${index}.groups`}
                rules={{
                  required: "この項目は入力必須です。",
                }}
                render={({ field }) => (
                  <Autocomplete
                    {...field}
                    options={groupsOptions}
                    getOptionLabel={(option: Groups) => option.name}
                    onChange={(_, newValue) => field.onChange(newValue)}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        required
                        error={!!errors.userGroup?.[index]?.groups}
                        helperText={errors.userGroup?.[index]?.groups?.message}
                        label="グループ"
                        sx={{ width: "18rem" }}
                        size="small"
                      />
                    )}
                  />
                )}
              />
            </Grid>
            <Grid>
              <Typography variant="body2">:</Typography>
            </Grid>
            <Grid>
              <Controller
                control={control}
                name={`userGroup.${index}.userGroupHistories.startDate`}
                rules={{
                  required: "この項目は入力必須です。",
                }}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="YYYY/MM/DD"
                      label="所属開始日"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) =>
                        field.onChange(value ? value.toDate() : null)
                      }
                      slotProps={{
                        calendarHeader: {
                          format: "YYYY年M月",
                        },
                        textField: {
                          required: true,
                          error:
                            !!errors.userGroup?.[index]?.userGroupHistories
                              ?.startDate,
                          helperText:
                            !!errors.userGroup?.[index]?.userGroupHistories
                              ?.startDate?.message,
                          size: "small",
                        },
                      }}
                      sx={{ width: "10rem" }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid>
              <Controller
                control={control}
                name={`userGroup.${index}.userGroupHistories.endDate`}
                render={({ field }) => (
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <DatePicker
                      format="YYYY/MM/DD"
                      label="所属終了日"
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(value) =>
                        field.onChange(value ? value.toDate() : null)
                      }
                      slotProps={{
                        calendarHeader: {
                          format: "YYYY年M月",
                        },
                        textField: {
                          size: "small",
                        },
                      }}
                      sx={{ width: "10rem" }}
                    />
                  </LocalizationProvider>
                )}
              />
            </Grid>
            <Grid>
              <Controller
                control={control}
                name={`userGroup.${index}.userGroupHistories.notes`}
                render={({ field }) => (
                  <TextField
                    {...field}
                    onChange={(e) => field.onChange(e.target.value)}
                    label="備考"
                    sx={{ width: "10rem" }}
                    size="small"
                  />
                )}
              />
            </Grid>
            <Grid>
              {value.userGroupHistories?.id ? (
                <Box sx={{ width: 36, height: 36 }} />
              ) : (
                <IconButton type="button" onClick={() => remove(index)}>
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Grid>
          </Grid>
        ))}
        <IconButton
          type="button"
          color="primary"
          onClick={() =>
            append({
              groups: null,
              userGroupHistories: {
                id: null,
                startDate: null,
                endDate: null,
                notes: "",
              },
            } as UserGroup)
          }
        >
          <Add fontSize="small" />
        </IconButton>
      </Box>
    );
  }
);

/**
 * ページングコンポーネント
 */
const PagingComponent = memo(
  ({
    position,
    sortAndFilteredData,
    page,
    setPage,
    pageSize,
  }: {
    position: "top" | "bottom";
    sortAndFilteredData: RecordUserDataModel[];
    page: number;
    setPage: React.Dispatch<React.SetStateAction<number>>;
    pageSize: number;
  }) => {
    return (
      <Box
        sx={{
          ml: 2,
          mt: position === "top" ? 1 : "auto",
          mb: 1,
          display: "flex",
          gap: 2,
          justifyContent: "flex-start",
          alignItems: "center",
        }}
      >
        {/* 件数表示 */}
        <div style={{ marginTop: 1, color: "#666", fontSize: "1rem" }}>
          全 {sortAndFilteredData.length} 件中{" "}
          {sortAndFilteredData.length === 0
            ? "0 件 〜 0 件"
            : `${(page - 1) * pageSize + 1} 件 〜 ${Math.min(
                page * pageSize,
                sortAndFilteredData.length
              )} 件`}
          を表示
        </div>
        <div>
          <Pagination
            count={Math.ceil(sortAndFilteredData.length / pageSize)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
            sx={{
              "& .MuiPaginationItem-root": {
                minWidth: "20px",
                height: "20px",
                padding: "1px",
              },
            }}
          />
        </div>
      </Box>
    );
  }
);

/**
 * RecordUserDataModel項目が1つでも変更されていればtrueを返す
 */
const compareModel = (a: RecordUserDataModel, b: RecordUserDataModel) => {
  // 2つのモデルの各項目(文字列)のいずれか不一致の場合、差異あり
  if (
    a.firstName !== b.firstName ||
    a.middleName !== b.middleName ||
    a.lastName !== b.lastName ||
    a.nickname !== b.nickname ||
    a.email !== b.email
  ) {
    return true;
  }
  // 2つのモデルの各項目(配列)のサイズが不一致の場合、差異あり
  if (
    (a.userRoles?.length || 0) !== (b.userRoles?.length || 0) ||
    (a.userProfiles?.length || 0) !== (b.userProfiles?.length || 0) ||
    (a.userGroup?.length || 0) !== (b.userGroup?.length || 0)
  ) {
    return true;
  }
  // 2つのモデルのユーザーロールの各項目が不一致の場合、差異あり
  if (
    a.userRoles &&
    b.userRoles &&
    !a.userRoles.every((aVal, i) => {
      const bVal = b.userRoles?.[i];
      return bVal && aVal.id === bVal.id;
    })
  ) {
    return true;
  }
  // 2つのモデルのユーザー情報の各項目が不一致の場合、差異あり
  if (
    a.userProfiles &&
    b.userProfiles &&
    !a.userProfiles.every((aVal, i) => {
      const bVal = b.userProfiles?.[i];
      return (
        bVal &&
        aVal.id === bVal.id &&
        aVal.key === bVal.key &&
        aVal.value === bVal.value
      );
    })
  ) {
    return true;
  }
  // 2つのモデルのユーザー所属情報の各項目が不一致の場合、差異あり
  if (
    a.userGroup &&
    b.userGroup &&
    !a.userGroup.every((aVal, i) => {
      const bVal = b.userGroup?.[i];
      return (
        bVal &&
        aVal.userGroupHistories &&
        bVal.userGroupHistories &&
        aVal.userGroupHistories.id === bVal.userGroupHistories.id &&
        aVal.userGroupHistories.startDate ===
          bVal.userGroupHistories.startDate &&
        aVal.userGroupHistories.endDate === bVal.userGroupHistories.endDate &&
        aVal.userGroupHistories.notes === bVal.userGroupHistories.notes &&
        aVal.groups &&
        bVal.groups &&
        aVal.groups.id === bVal.groups.id
      );
    })
  ) {
    return true;
  }
  // 上記以外の場合、差異なし
  return false;
};

/**
 * ソートの型定義
 */
type SortConfigType = {
  key: keyof RecordUserDataModel;
  asc: boolean;
};

/**
 * ユーザー管理テーブルコンポーネント
 * - ユーザー一覧をページング付きテーブルで表示し、インライン編集・削除が可能
 * - 変更されたユーザーのみ保存対象としてマークする
 */
const UserManageTable = ({
  page,
  setPage,
  pageSize,
  filteredData,
  rows,
  setRows,
  selectedRows,
  setSelectedRows,
  visibleColumns,
  rolesOptions,
  groupsOptions,
  setUpdateRows,
}: {
  page: number;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  pageSize: number;
  filteredData: RecordUserDataModel[];
  rows: RecordUserDataModel[];
  setRows: React.Dispatch<React.SetStateAction<RecordUserDataModel[]>>;
  selectedRows: Set<string>;
  setSelectedRows: React.Dispatch<React.SetStateAction<Set<string>>>;
  visibleColumns: Set<string>;
  rolesOptions: Roles[];
  groupsOptions: Groups[];
  setUpdateRows?: React.Dispatch<React.SetStateAction<RecordUserDataModel[]>>;
}) => {
  const isMobile = useMediaQuery("(max-width:600px)"); // 600px 以下でモバイル表示
  const [hoveredColumn, setHoveredColumn] = useState<string | null>(null);
  const [editRows, setEditRows] = useState<Set<string>>(new Set());

  const [sortConfig, setSortConfig] = useState<SortConfigType>({
    key: "userId",
    asc: true,
  });

  /** ソート&フィルター機能 */
  const sortAndFilteredData = useMemo(() => {
    let result = [...filteredData];
    const { key, asc } = sortConfig ?? {};
    result.sort((a, b) => {
      const aValue = a[key ?? "userId"];
      const bValue = b[key ?? "userId"];
      return asc
        ? String(aValue).localeCompare(String(bValue))
        : String(bValue).localeCompare(String(aValue));
    });
    return result;
  }, [filteredData, sortConfig]);

  /** ページング機能 */
  const paginatedData = useMemo(() => {
    const startIndex = (page - 1) * pageSize;
    return sortAndFilteredData.slice(startIndex, startIndex + pageSize);
  }, [sortAndFilteredData, page, pageSize]);

  /** 行更新処理 */
  const handleUpdateRow = useCallback(
    (targetRow: RecordUserDataModel) => {
    
      // rows から編集対象ユーザーの元データを検索
      // 後続の変数名との衝突を避けるため originalRow にリネーム
      const originalRow = rows.filter((v) => v.userId === targetRow.userId)[0];

      // 元データが見つかり、かつデータに変更があるか確認
      if (originalRow && compareModel(originalRow, targetRow)) {

        // 編集対象ユーザーの新しい userProfile IDs セットを構築
        // このセットは、どの古い userProfiles を削除マークすべきかを判断するために使用
        const updateUserProfilesIds = new Set(
          targetRow.userProfiles?.map((i) => i.id)
        );

        // 最終的な updateRow データを構築、map ループの外側で実行され、一度だけ実行される
        const updateRow: RecordUserDataModel = {
          ...targetRow,  // 編集後の全フィールドを使用
          // userProfiles の特殊処理：古い profiles を isDeleted でマーク
          userProfiles: originalRow.userProfiles  //編集対象ユーザー自身の元 profiles を使用
            ? [
                // 編集後の新しい userProfiles を追加
                ...(targetRow.userProfiles ?? []),
                // 削除された userProfiles を検出（元データには存在するが新データには存在しない）
                // isDeleted: true でマーク
                ...originalRow.userProfiles
                  ?.filter((i) => !updateUserProfilesIds.has(i.id))  // 削除された項目をフィルタ
                  .map((i) => ({ ...i, isDeleted: true })),  // 削除マーク
              ]
            : null,
        };

        // rows 状態を更新（全ユーザーのデータリスト）
        // 編集されたユーザーのみ更新、その他のユーザーはそのまま
        setRows((prevRows) =>
          prevRows.map((currentRow) =>
            currentRow.userId === targetRow.userId ? updateRow : currentRow
          )
        );

        // updateRows 状態を更新（バックエンドに送信するユーザーリスト）
        // 一度だけ呼び出し、ループ内では呼び出さない
        if (setUpdateRows) {
          setUpdateRows((prevUpdateRows) => [
            // 該当ユーザーの以前の更新レコードを削除（存在する場合）
            ...prevUpdateRows.filter((v) => v.userId !== targetRow.userId),
            // 新しい更新レコードを追加
            updateRow,
          ]);
        }
      }
    },
    [rows, setRows, setUpdateRows]
  );

  return (
    <>
      <PagingComponent
        position="top"
        sortAndFilteredData={sortAndFilteredData}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
      />
      <TableContainer
        component={Paper}
        sx={{
          mx: isMobile || !setUpdateRows ? 0 : 1,
          mb: 2,
          overflow: "auto", // スクロールを表示
          maxHeight: "calc(100vh - 350px)",
          maxWidth: isMobile || !setUpdateRows ? "100%" : "calc(100vw - 80px)",
        }}
      >
        <Table
          sx={{ tableLayout: "auto", minWidth: 800 }}
          aria-label="customized table"
          stickyHeader
        >
          <TableHead>
            <TableRow>
              <StyledTableCell>
                <Tooltip
                  title={
                    selectedRows.size === sortAndFilteredData.length &&
                    sortAndFilteredData.length > 0
                      ? "すべて選択解除"
                      : "すべて選択"
                  }
                >
                  <Checkbox
                    checked={
                      selectedRows.size === sortAndFilteredData.length &&
                      sortAndFilteredData.length > 0
                    }
                    onChange={() =>
                      setSelectedRows(
                        selectedRows.size === sortAndFilteredData.length &&
                        sortAndFilteredData.length > 0
                          ? new Set()
                          : new Set(
                              sortAndFilteredData.map((row) => row.userId ?? "")
                            )
                      )
                    }
                  />
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell>
                <Tooltip
                  title={
                    editRows.size === paginatedData.length
                      ? "すべて閲覧モード"
                      : "すべて編集モード"
                  }
                >
                  <IconButton
                    type="button"
                    onClick={() => {
                      setEditRows(
                        editRows.size === paginatedData.length
                          ? new Set()
                          : new Set(
                              paginatedData.map((row) => row.userId ?? "")
                            )
                      );
                    }}
                    sx={{ mr: 1 }}
                  >
                    {editRows.size === paginatedData.length ? (
                      <EditOff fontSize="small" className="text-gray-500" />
                    ) : (
                      <Edit fontSize="small" className="text-gray-500" />
                    )}
                  </IconButton>
                </Tooltip>
              </StyledTableCell>
              <StyledTableCell />
              {Object.entries(columnLabels).map(([key, label]) => {
                const isActive = sortConfig?.key === key;
                const asc = sortConfig?.asc;
                return visibleColumns.has(key) ? (
                  <StyledTableCell
                    key={key}
                    onClick={() => {
                      setPage(1); // 第一ページに戻る
                      setSortConfig(
                        sortConfig?.key === key
                          ? {
                              key: key as keyof RecordUserDataModel,
                              asc: !sortConfig.asc,
                            }
                          : {
                              key: key as keyof RecordUserDataModel,
                              asc: true,
                            }
                      );
                    }}
                    onMouseEnter={() => setHoveredColumn(key)}
                    onMouseLeave={() => setHoveredColumn(null)}
                    style={{ cursor: "pointer", whiteSpace: "nowrap" }}
                  >
                    <div
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                      }}
                    >
                      {label}
                      {(isActive || hoveredColumn === key) &&
                        (asc && isActive ? (
                          <ExpandLess
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : !asc && isActive ? (
                          <ExpandMore
                            fontSize="small"
                            className="ml-1 text-gray-500"
                          />
                        ) : (
                          <ExpandMore
                            fontSize="small"
                            className="ml-1 text-gray-300"
                          />
                        ))}
                    </div>
                  </StyledTableCell>
                ) : null;
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedData.map((row, index) => (
              <CustomTableRow
                initRow={row}
                index={index}
                selectedRows={selectedRows}
                setSelectedRows={setSelectedRows}
                editRows={editRows}
                setEditRows={setEditRows}
                visibleColumns={visibleColumns}
                rolesOptions={rolesOptions}
                groupsOptions={groupsOptions}
                handleUpdateRow={handleUpdateRow}
                allowEmailEdit={!setUpdateRows}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <PagingComponent
        position="bottom"
        sortAndFilteredData={sortAndFilteredData}
        page={page}
        setPage={setPage}
        pageSize={pageSize}
      />
    </>
  );
};

export default UserManageTable;
